import React, { useMemo, useState } from 'react';
import { KpiCards } from '../components/KpiCards';
import { ControlBar } from '../components/ControlBar';
import { EscalationTable } from '../components/EscalationTable';
import { BulkActionBar } from '../components/BulkActionBar';
import { RetentionModal } from '../components/RetentionModal';
import { CloseEscalationModal } from '../components/CloseEscalationModal';
import { LoanDetailPanel } from '../components/LoanDetailPanel';
import { mockLoans } from '../data/mockData';
import { Loan, LoanStatus, QuickFilter } from '../types';
export function EscalationDashboard() {
  const [loans, setLoans] = useState<Loan[]>(mockLoans);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<LoanStatus>('Open');
  const [activeQuickFilter, setActiveQuickFilter] = useState<QuickFilter>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedLoanForDetails, setSelectedLoanForDetails] = useState<Loan | null>(null);
  // Retention modal state
  const [isRetentionModalOpen, setIsRetentionModalOpen] = useState(false);
  const [loansToReassign, setLoansToReassign] = useState<string[]>([]);
  // Close escalation modal state
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [loanToClose, setLoanToClose] = useState<Loan | null>(null);
  // Derived KPIs
  const totalOpen = loans.filter(l => l.status === 'Open').length;
  const closingSoon = loans.filter(l => {
    if (l.status !== 'Open') return false;
    const diff = new Date(l.closingDate).getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24)) <= 3;
  }).length;
  const movedToday = 12;
  // Filtering Logic
  const filteredLoans = useMemo(() => {
    return loans.filter(loan => {
      if (loan.status !== statusFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesSearch = loan.borrowerName.toLowerCase().includes(q) || loan.loanName.toLowerCase().includes(q) || loan.loanId.includes(q);
        if (!matchesSearch) return false;
      }
      if (activeQuickFilter === 'Closing Soon') {
        const diff = new Date(loan.closingDate).getTime() - new Date().getTime();
        if (Math.ceil(diff / (1000 * 60 * 60 * 24)) > 3) return false;
      }
      if (activeQuickFilter === 'Lock Expiring') {
        const diff = new Date(loan.lockExpiration).getTime() - new Date().getTime();
        if (Math.ceil(diff / (1000 * 60 * 60 * 24)) > 5) return false;
      }
      if (activeQuickFilter === 'High Loan Amount') {
        if (loan.loanAmount < 600000) return false;
      }
      return true;
    });
  }, [loans, searchQuery, statusFilter, activeQuickFilter]);
  // Selection handlers
  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const handleToggleSelectAll = () => {
    if (selectedIds.length === filteredLoans.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredLoans.map(l => l.id));
    }
  };
  // Move to Retention handlers
  const handleMoveToRetention = (e: React.MouseEvent, loanId: string) => {
    e.stopPropagation();
    setLoansToReassign([loanId]);
    setIsRetentionModalOpen(true);
  };
  const handleBulkReassign = () => {
    setLoansToReassign(selectedIds);
    setIsRetentionModalOpen(true);
  };
  const handleConfirmReassign = (processor: string, loa: string) => {
    setLoans(prev => prev.map(loan => loansToReassign.includes(loan.id) ? {
      ...loan,
      status: 'Closed' as const,
      notes: `Reassigned to Retention (${processor} / ${loa}). ` + loan.notes
    } : loan));
    setIsRetentionModalOpen(false);
    setSelectedIds([]);
    setLoansToReassign([]);
    if (selectedLoanForDetails && loansToReassign.includes(selectedLoanForDetails.id)) {
      setSelectedLoanForDetails(null);
    }
  };
  // Close Escalation handlers
  const handleCloseEscalation = (e: React.MouseEvent, loanId: string) => {
    e.stopPropagation();
    const loan = loans.find(l => l.id === loanId) || null;
    setLoanToClose(loan);
    setIsCloseModalOpen(true);
  };
  const handleConfirmClose = () => {
    if (!loanToClose) return;
    setLoans(prev => prev.map(loan => loan.id === loanToClose.id ? {
      ...loan,
      status: 'Closed' as const,
      notes: `Escalation closed — loan remains with ${loan.processor} / ${loan.loa}. ` + loan.notes
    } : loan));
    if (selectedLoanForDetails?.id === loanToClose.id) {
      setSelectedLoanForDetails(null);
    }
    setIsCloseModalOpen(false);
    setLoanToClose(null);
  };
  // Bulk close escalation
  const handleBulkCloseEscalation = () => {
    setLoans(prev => prev.map(loan => selectedIds.includes(loan.id) ? {
      ...loan,
      status: 'Closed' as const,
      notes: `Escalation closed — loan remains with ${loan.processor} / ${loan.loa}. ` + loan.notes
    } : loan));
    setSelectedIds([]);
  };
  return <div className="min-h-screen bg-[#FAFAFA] text-gray-900 font-sans selection:bg-blue-100 selection:text-blue-900 pb-24" data-id="element-235">
      <main className="max-w-[1600px] mx-auto px-6 py-8" data-id="element-236">
        <div className="mb-8" data-id="element-237">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-238">
            Escalation Queue
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-239">
            Retention Review · Processing Manager Dashboard
          </p>
        </div>

        <KpiCards totalOpen={totalOpen} closingSoon={closingSoon} movedToday={movedToday} data-id="element-240" />

        <ControlBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} statusFilter={statusFilter} setStatusFilter={setStatusFilter} activeQuickFilter={activeQuickFilter} setActiveQuickFilter={setActiveQuickFilter} data-id="element-241" />

        <EscalationTable loans={filteredLoans} selectedIds={selectedIds} onToggleSelect={handleToggleSelect} onToggleSelectAll={handleToggleSelectAll} onRowClick={setSelectedLoanForDetails} onMoveToRetention={handleMoveToRetention} onCloseEscalation={handleCloseEscalation} data-id="element-242" />
      </main>

      <BulkActionBar selectedCount={selectedIds.length} onClearSelection={() => setSelectedIds([])} onReassign={handleBulkReassign} onCloseEscalation={handleBulkCloseEscalation} data-id="element-243" />

      <RetentionModal isOpen={isRetentionModalOpen} onClose={() => setIsRetentionModalOpen(false)} selectedCount={loansToReassign.length} onConfirm={handleConfirmReassign} data-id="element-244" />

      <CloseEscalationModal isOpen={isCloseModalOpen} onClose={() => {
      setIsCloseModalOpen(false);
      setLoanToClose(null);
    }} loan={loanToClose} onConfirm={handleConfirmClose} data-id="element-245" />

      <LoanDetailPanel loan={selectedLoanForDetails} onClose={() => setSelectedLoanForDetails(null)} onReassign={id => {
      setLoansToReassign([id]);
      setIsRetentionModalOpen(true);
    }} data-id="element-246" />
    </div>;
}